import React, { useRef, useState } from 'react'
import { Select } from 'sutra/components/Select/Select'

import {
  CardActionButton,
  CardRoot,
  FilterChip,
  GroupPrefix,
  RevealOnCardHover,
} from 'components/filter-card/filter-card.styles'
import { FilterCardAccentBar } from 'components/FilterCardAccentBar'
import { InheritedFilterBadge } from 'components/InheritedFilterBadge'
import {
  OperatorPopover as KeywordOperatorPopover,
  type OperatorType,
} from 'features/charts-v2/components/KeywordOperatorPopover'
import { useTypedTokenCommit } from 'features/charts-v2/hooks/use-typed-token-commit'
import { parseKeywordInput } from 'features/charts-v2/utils/parse-keyword-input'
import { FilterOption } from 'features/filters/types'

type SelectedFilterOption = {
  field: string
  operator: OperatorType
  values: string[]
}

interface FilterCardProps {
  filter: FilterOption
  updateFilterSelection: (
    field: string,
    operator: OperatorType,
    values: string[],
  ) => void
  getFilterSelection: (field: string) => SelectedFilterOption | null
  onDelete?: () => void
  // Presence renders this card read-only — inherited from a parent scope
  // rather than owned by the tile. See `FilterCard.tsx`'s (charts-v2) doc
  // comment on its equivalent prop for the full rationale. `sourceLabel` is
  // still supplied by every caller (see that file's equivalent prop) but no
  // longer read here — `InheritedFilterBadge` stopped needing it.
  inherited?: { supersededNote: string | null; sourceLabel: string }
  // Ownership accent bar, bundled into one prop to keep this component's own
  // prop count down — the shape `FilterCard.tsx`'s (charts-v2) `accentBar`
  // takes. `onEditSource` also feeds the lock badge when `inherited`.
  accentBar?: {
    accent: 'local' | 'global'
    tileKind?: 'chart' | 'widget'
    onEditSource?: () => void
  }
  // The picker group this card sits under ("Search"), shown as a muted
  // `Group > ` before the title. Omitted when hierarchy is off.
  groupLabel?: string
}

export const FilterCard: React.FC<FilterCardProps> = ({
  filter,
  updateFilterSelection,
  getFilterSelection,
  onDelete,
  inherited,
  accentBar,
  groupLabel,
}) => {
  const isReadOnly = inherited != null
  const [isOperatorPopoverOpen, setIsOperatorPopoverOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const operatorRef = useRef<HTMLSpanElement>(null)

  // Get current selection from global state
  const currentSelection = getFilterSelection(filter.field)
  const selectedValues = currentSelection?.values || []
  const selectedOperator = currentSelection?.operator || 'Includes all'

  const handleSelectionChange = (newSelectedValues: string[]): void => {
    // Select splits on the token separators but leaves wrapping quotes intact;
    // run each token through the shared parser to strip quotes, drop empties,
    // and dedupe (and split any token that still carries a delimiter).
    const cleanedValues = Array.from(
      new Set(newSelectedValues.flatMap(parseKeywordInput)),
    )
    updateFilterSelection(filter.field, selectedOperator, cleanedValues)
  }

  const token = useTypedTokenCommit(selectedValues, handleSelectionChange)

  const handleOperatorSelect = (newOperator: OperatorType): void => {
    updateFilterSelection(filter.field, newOperator, selectedValues)
  }

  const handleDeleteFilter = (): void => {
    if (onDelete) {
      onDelete()
    } else {
      // Fallback: Remove the filter by passing an empty values array
      updateFilterSelection(filter.field, selectedOperator, [])
    }
  }

  return (
    <>
      <CardRoot ref={cardRef} $disabled={false} $accent={accentBar?.accent}>
        {accentBar && <FilterCardAccentBar {...accentBar} />}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span
                  style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '14px',
                  }}
                >
                  {groupLabel && (
                    <GroupPrefix>
                      {groupLabel} {'> '}
                    </GroupPrefix>
                  )}
                  {filter.alias || filter.field}
                </span>
                {inherited && (
                  <RevealOnCardHover>
                    <InheritedFilterBadge
                      onEditSource={accentBar?.onEditSource}
                    />
                  </RevealOnCardHover>
                )}
              </div>

              {/* Delete Button - only visible on card hover, via CSS */}
              {!isReadOnly && (
                <RevealOnCardHover>
                  <CardActionButton
                    type="button"
                    $variant="danger"
                    aria-label="Remove filter"
                    title="Remove filter"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteFilter()
                    }}
                  >
                    ×
                  </CardActionButton>
                </RevealOnCardHover>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-top',
                fontSize: '14px',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <FilterChip
                ref={operatorRef}
                $open={isOperatorPopoverOpen}
                $disabled={isReadOnly}
                $color="#6b7280"
                onClick={
                  isReadOnly
                    ? undefined
                    : () => setIsOperatorPopoverOpen(!isOperatorPopoverOpen)
                }
              >
                {selectedOperator}
              </FilterChip>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                className="keyword-select"
                placeholder="Type keywords and press enter to add"
                tokenSeparators={[',', '\n']}
                options={[]}
                value={selectedValues}
                onChange={(picked) => handleSelectionChange(picked)}
                open={false}
                suffixIcon={null}
                searchValue={token.typed}
                onSearch={token.setTyped}
                onBlur={token.commitOnBlur}
                disabled={isReadOnly}
              />
            </div>
          </div>
        </div>
      </CardRoot>

      {isOperatorPopoverOpen && !isReadOnly && (
        <KeywordOperatorPopover
          onOperatorSelect={handleOperatorSelect}
          onClose={() => setIsOperatorPopoverOpen(false)}
          selectedOperator={selectedOperator}
          triggerRef={operatorRef}
          cardRef={cardRef}
        />
      )}
    </>
  )
}

export type { SelectedFilterOption }
